// =============================================================================
// components/community/ChannelInfoSheet.tsx — the channel info bottom sheet from
// the reference design (IMG_5872): "# channel" title, description (+ Edit for
// the server owner), disclosure rows with REAL counts, and a destructive
// "Leave Chat" row. Recolored to the current lavender/purple system.
//
// DATA (all real endpoints, fetched lazily when the sheet opens):
//   GET  /channels/:id                → description/topic + server{id,name,ownerId}
//   GET  /servers/:sid                → ServerMember[] (member count; bare {server})
//   GET  /threads/channel/:id?limit=1 → data.pagination.total (thread count)
//   PATCH /channels/:id { description } — owner-only edit
//   POST /servers/:sid/leave          — "Leave Chat"
//
// NO dead rows: "Pinned Messages" from the reference is NOT rendered — the
// backend has pin/unpin actions but no pins-list endpoint to route to or count
// from. Threads renders as a count stat (no chevron) because no thread screen
// exists in the RN app yet; Members routes to the community interior's Key Info.
// =============================================================================
import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal, View, Text, TouchableOpacity, Pressable, TextInput,
  StyleSheet, ActivityIndicator, Alert,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons, IoniconName } from '../ui/Icon';
import { GlassSheet } from '../glass';
import { colors, spacing, radius, fonts } from '../../constants/theme';
import { api } from '../../services/api';

interface ChannelInfo {
  description: string;
  serverId: string | null;
  serverName: string;
  ownerId: string;
}

export default function ChannelInfoSheet({
  visible,
  onClose,
  channelId,
  channelName,
  meId,
  onOpenMembers,
  onLeft,
}: {
  visible: boolean;
  onClose: () => void;
  channelId: string;
  channelName: string;
  /** Current user id — enables the owner-only Edit affordance. */
  meId: string;
  /** Routes to the community interior (Key Info) for the channel's server. */
  onOpenMembers: (serverId: string) => void;
  /** Called after successfully leaving the server ("Leave Chat"). */
  onLeft: () => void;
}) {
  const insets = useSafeAreaInsets();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<ChannelInfo | null>(null);
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [threadCount, setThreadCount] = useState<number | null>(null);
  // Owner description editing (inline).
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // Channel detail → description + owning server.
    const res = await api<any>(`/channels/${encodeURIComponent(channelId)}`);
    if (res.success && res.data) {
      const c = res.data;
      const server = c.server ?? c.Server ?? null;
      const next: ChannelInfo = {
        description: c.description || c.topic || '',
        serverId: server?.id ?? c.serverId ?? null,
        serverName: server?.name ?? '',
        ownerId: server?.ownerId ?? '',
      };
      setInfo(next);

      // Member count — GET /servers/:sid returns the bare { server } envelope
      // with the full ServerMember include (members-only; ignore failures).
      if (next.serverId) {
        api<any>(`/servers/${encodeURIComponent(next.serverId)}`).then((sres) => {
          const s = sres.success ? (sres.data?.server ?? sres.data) : null;
          const members = Array.isArray(s?.ServerMember) ? s.ServerMember.length
            : Array.isArray(s?.members) ? s.members.length : null;
          if (members !== null) setMemberCount(members);
        }).catch(() => {});
      }
    } else {
      setInfo(null);
    }

    // Thread count (real endpoint; hide the stat when it doesn't answer).
    api<any>(`/threads/channel/${encodeURIComponent(channelId)}?limit=1`).then((tres) => {
      const total = tres.success ? tres.data?.pagination?.total : null;
      if (typeof total === 'number') setThreadCount(total);
    }).catch(() => {});

    setLoading(false);
  }, [channelId]);

  useEffect(() => {
    if (visible) {
      setEditing(false);
      load();
    }
  }, [visible, load]);

  const isOwner = !!meId && !!info?.ownerId && meId === info.ownerId;

  async function saveDescription() {
    if (saving) return;
    setSaving(true);
    const res = await api(`/channels/${encodeURIComponent(channelId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ description: draft.trim() || null }),
    });
    setSaving(false);
    if (res.success) {
      setInfo((prev) => (prev ? { ...prev, description: draft.trim() } : prev));
      setEditing(false);
    } else {
      Alert.alert('Could not save', res.error || 'Please try again.');
    }
  }

  function handleLeave() {
    const sid = info?.serverId;
    if (!sid || leaving) return;
    Alert.alert('Leave Chat', `Leave ${info?.serverName || 'this community'}? You'll lose access to its channels.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Leave', style: 'destructive', onPress: async () => {
          setLeaving(true);
          const res = await api(`/servers/${encodeURIComponent(sid)}/leave`, { method: 'POST' });
          setLeaving(false);
          if (res.success) {
            onClose();
            onLeft();
          } else {
            Alert.alert('Could not leave', res.error || 'Owners cannot leave their own community.');
          }
        },
      },
    ]);
  }

  const Row = ({ icon, label, value, onPress }: {
    icon: IoniconName; label: string; value: string; onPress?: () => void;
  }) => (
    <TouchableOpacity
      style={styles.row}
      activeOpacity={onPress ? 0.7 : 1}
      onPress={onPress}
      disabled={!onPress}
      accessibilityRole={onPress ? 'button' : 'text'}
      accessibilityLabel={`${label}: ${value}`}
    >
      <View style={styles.rowIcon}>
        <Ionicons name={icon} size={16} color={colors.primary} />
      </View>
      <Text style={styles.rowLabel}>{label}</Text>
      <Text style={styles.rowValue}>{value}</Text>
      {onPress ? <Ionicons name="chevron-forward" size={16} color={colors.faint} /> : null}
    </TouchableOpacity>
  );

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose} accessibilityLabel="Close channel info" />
      <View style={[styles.sheetWrap, { paddingBottom: Math.max(insets.bottom, spacing.lg) }]}>
        <GlassSheet>
          <Text style={styles.title} numberOfLines={1}># {channelName}</Text>

          {loading ? (
            <View style={styles.loading}><ActivityIndicator color={colors.primary} /></View>
          ) : (
            <>
              {/* Description (+ owner-only inline edit via PATCH /channels/:id) */}
              {editing ? (
                <View style={styles.editWrap}>
                  <TextInput
                    style={styles.editInput}
                    value={draft}
                    onChangeText={setDraft}
                    placeholder="What is this channel about?"
                    placeholderTextColor={colors.muted}
                    multiline
                    maxLength={500}
                    autoFocus
                  />
                  <View style={styles.editActions}>
                    <TouchableOpacity onPress={() => setEditing(false)} style={styles.editCancel} accessibilityRole="button" accessibilityLabel="Cancel editing">
                      <Text style={styles.editCancelText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={saveDescription} disabled={saving} style={styles.editSave} accessibilityRole="button" accessibilityLabel="Save description">
                      {saving ? <ActivityIndicator size="small" color="#fff" /> : <Text style={styles.editSaveText}>Save</Text>}
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <>
                  <Text style={styles.desc}>
                    {info?.description || 'No description yet.'}
                  </Text>
                  {isOwner && (
                    <TouchableOpacity
                      style={styles.editPill}
                      onPress={() => { setDraft(info?.description || ''); setEditing(true); }}
                      activeOpacity={0.85}
                      accessibilityRole="button"
                      accessibilityLabel="Edit channel description"
                    >
                      <Text style={styles.editPillText}>Edit</Text>
                    </TouchableOpacity>
                  )}
                </>
              )}

              {/* Disclosure/stat rows — only rows backed by real data render. */}
              <View style={styles.rows}>
                {info?.serverId && memberCount !== null && (
                  <Row
                    icon="people"
                    label="Members"
                    value={memberCount.toLocaleString()}
                    onPress={() => { onClose(); onOpenMembers(info.serverId!); }}
                  />
                )}
                {threadCount !== null && (
                  // Stat only (no chevron/route): threads have a real count
                  // endpoint but no screen in the RN app yet.
                  <Row icon="git-branch-outline" label="Threads" value={threadCount.toLocaleString()} />
                )}
                {/* Pinned Messages intentionally omitted — no pins-list endpoint. */}
              </View>

              {/* Destructive: leave the owning server. */}
              {!!info?.serverId && (
                <TouchableOpacity
                  style={styles.leaveRow}
                  activeOpacity={0.7}
                  onPress={handleLeave}
                  disabled={leaving}
                  accessibilityRole="button"
                  accessibilityLabel="Leave chat"
                >
                  <View style={[styles.rowIcon, { backgroundColor: colors.error + '15' }]}>
                    {leaving
                      ? <ActivityIndicator size="small" color={colors.error} />
                      : <Ionicons name="exit-outline" size={16} color={colors.error} />}
                  </View>
                  <Text style={[styles.rowLabel, { color: colors.error }]}>Leave Chat</Text>
                </TouchableOpacity>
              )}
            </>
          )}
        </GlassSheet>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(26,26,46,0.35)' },
  sheetWrap: { paddingHorizontal: spacing.md },
  // Reference (IMG_5872) renders the "# channel" title in the accent color —
  // ours is the brand primary instead of the old orange.
  title: { ...fonts.bold, fontSize: 18, color: colors.primary, marginBottom: spacing.sm },
  loading: { paddingVertical: spacing.xxl, alignItems: 'center' },
  desc: { ...fonts.regular, fontSize: 14, lineHeight: 20, color: colors.ink2 },
  editPill: {
    alignSelf: 'flex-start', marginTop: spacing.md,
    backgroundColor: colors.primary, borderRadius: radius.full,
    paddingHorizontal: spacing.xl, paddingVertical: spacing.sm,
  },
  editPillText: { ...fonts.semibold, fontSize: 13, color: '#FFFFFF' },
  editWrap: { gap: spacing.sm },
  editInput: {
    backgroundColor: 'rgba(255,255,255,0.9)', borderRadius: radius.md,
    borderWidth: 1, borderColor: 'rgba(0,0,0,0.06)',
    padding: spacing.md, minHeight: 72, fontSize: 14, color: colors.ink,
    textAlignVertical: 'top', ...fonts.regular,
  },
  editActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm },
  editCancel: { paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  editCancelText: { ...fonts.semibold, fontSize: 13, color: colors.muted },
  editSave: {
    backgroundColor: colors.primary, borderRadius: radius.full,
    paddingHorizontal: spacing.xl, paddingVertical: spacing.sm,
    minWidth: 72, alignItems: 'center',
  },
  editSaveText: { ...fonts.semibold, fontSize: 13, color: '#FFFFFF' },
  rows: { marginTop: spacing.lg },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.md,
    backgroundColor: 'rgba(255,255,255,0.8)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.9)',
    borderRadius: radius.lg, paddingHorizontal: spacing.lg, paddingVertical: spacing.md,
    marginBottom: spacing.sm,
  },
  rowIcon: {
    width: 30, height: 30, borderRadius: 9, backgroundColor: colors.primary + '15',
    alignItems: 'center', justifyContent: 'center',
  },
  rowLabel: { ...fonts.semibold, fontSize: 15, color: colors.ink, flex: 1 },
  rowValue: { ...fonts.semibold, fontSize: 14, color: colors.muted },
  leaveRow: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.md,
    backgroundColor: 'rgba(255,255,255,0.8)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.9)',
    borderRadius: radius.lg, paddingHorizontal: spacing.lg, paddingVertical: spacing.md,
  },
});
